import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  CardActions,
  Grid,
  TextField,
  InputAdornment,
  Chip,
} from "@mui/material";
import {
  Search,
  Edit,
  Delete,
  CalendarToday,
  LocationOn,
  Add,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import axios from "axios";
import config from "../config";
import { Evento } from "./Evento";

const EventoList: React.FC = () => {
  const [eventos, setEventos] = useState<Evento[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchEventos();
  }, []);

  const fetchEventos = async () => {
    try {
      setLoading(true);
      const response = await axios.get<Evento[]>(
        `${config.backendUrl}/api/Eventos/proximos`
      );
      setEventos(response.data);
    } catch (error) {
      console.error("Error fetching eventos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    try {
      await axios.delete(`${config.backendUrl}/api/Eventos/${id}`);
      fetchEventos();
    } catch (error) {
      console.error("Error deleting evento:", error);
    }
  };

  const filteredEventos = eventos.filter(
    (evento) =>
      evento.titulo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      evento.lugar.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const formatDate = (dateString: Date | null) => {
    if (!dateString) return "";
    const date =
      typeof dateString === "string" ? new Date(dateString) : dateString;
    const options: Intl.DateTimeFormatOptions = {
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return date.toLocaleDateString("es-AR", options);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" fontWeight="bold">
Eventos en Buenos Aires        </Typography>
        <Button
          component={Link}
          to="/evento/crear"
          variant="contained"
          color="primary"
          startIcon={<Add />}
        >
          Nuevo Evento
        </Button>
      </Box>

      <TextField
        fullWidth
        variant="outlined"
        placeholder="Buscar eventos..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        sx={{ mb: 3 }}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <Search />
            </InputAdornment>
          ),
        }}
      />

      {loading ? (
        <Typography>Cargando eventos...</Typography>
      ) : filteredEventos.length === 0 ? (
        <Typography>No se encontraron eventos</Typography>
      ) : (
        <Grid container spacing={3}>
          {filteredEventos.map((evento) => (
            <Grid size={{ xs: 12, sm: 6, md: 4 }} key={evento.id}>
                <Card
                 component={Link}
                 to={`/eventos/${evento.id}`}
                 sx={{
                   display: "flex",
                   flexDirection: "column",
                   height: "100%",
                   textDecoration: "none",  // <== esto quita el subrayado
                   color: "inherit"         // <== mantiene el color normal
                 }}
                >
                  {evento.imagenUrl && (
                    <CardMedia
                      component="img"
                      height="180"
                      image={`${config.backendUrl}${evento.imagenUrl}`}
                      alt={evento.titulo}
                      sx={{ objectFit: "cover" }}
                    />
                  )}
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Typography variant="h6" fontWeight="bold">
                      {evento.titulo}
                    </Typography>
                    <Chip label={evento.tipo} size="small" sx={{ mb: 1 }} />
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      gutterBottom
                    >
                      {evento.descripcion?.substring(0, 80)}...
                    </Typography>
                    <Box display="flex" alignItems="center" mb={1}>
                      <LocationOn sx={{ mr: 1, fontSize: 18 }} color="action" />
                      <Typography variant="body2" color="text.secondary">
                        {evento.direccion}
                      </Typography>
                    </Box>
                    <Box display="flex" alignItems="center">
                      <CalendarToday
                        sx={{ mr: 1, fontSize: 18 }}
                        color="action"
                      />
                      <Typography variant="body2" color="text.secondary">
                        {formatDate(evento.fechaInicio)}
                      </Typography>
                    </Box>
                  </CardContent>
                  <CardActions></CardActions>
                </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default EventoList;
